import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { FiBarChart2, FiPlus, FiTrash2, FiEdit2, FiX, FiCalendar, FiTrendingUp, FiTarget, FiDollarSign, FiUsers, FiActivity, FiSave, FiMapPin, FiCheck } from 'react-icons/fi';

import { API_URL } from '../config';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function Reports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [reportFilter, setReportFilter] = useState('mine'); // 'mine', 'all', or a numeric user id
  const [reportLocationFilter, setReportLocationFilter] = useState(''); // '' = all locations
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('reports'); // reports, add, benchmarks, summary
  const [error, setError] = useState(null);
  const [editingReport, setEditingReport] = useState(null);

  // Form state
  const [formYear, setFormYear] = useState(new Date().getFullYear());
  const [formMonth, setFormMonth] = useState(new Date().getMonth() + 1);
  const [formPatientVisits, setFormPatientVisits] = useState('');
  const [formNewPatients, setFormNewPatients] = useState('');
  const [formServices, setFormServices] = useState('');
  const [formCollections, setFormCollections] = useState('');
  const [formReExams, setFormReExams] = useState('');
  const [formOfficeLocation, setFormOfficeLocation] = useState('');
  const [officeLocations, setOfficeLocations] = useState([]);
  const [saving, setSaving] = useState(false);

  // Benchmarks state
  const [benchmarks, setBenchmarks] = useState({
    standardAveragePatientVisits: 0,
    standardAverageNewPatients: 0,
    standardPatientVisitAverage: 0,
    standardServices: 0,
    standardServicesPerVisit: 0,
    standardCollections: 0,
    standardCollectionsPerVisit: 0,
    standardReExams: 0
  });
  const [savingBenchmarks, setSavingBenchmarks] = useState(false);

  // Summary state
  const [summaryYear, setSummaryYear] = useState(new Date().getFullYear());
  const [summaryLocation, setSummaryLocation] = useState('');
  const [summaryUser, setSummaryUser] = useState('mine');
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    loadReports();
    loadBenchmarks();
    loadOfficeLocations();
    if (user?.role === 'admin') {
      loadAllReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReports = async () => {
    try {
      setError(null);
      const response = await axios.get(`${API_URL}/reports`);
      setReports(response.data.reports || []);
    } catch (err) {
      console.error('Error loading reports:', err);
      setError('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const loadAllReports = async () => {
    try {
      const response = await axios.get(`${API_URL}/reports/all`);
      setAllReports(response.data.reports || []);
      setStaffUsers(response.data.users || []);
    } catch (err) {
      console.error('Error loading all reports:', err);
    }
  };

  // Admin location management state
  const [newLocationName, setNewLocationName] = useState('');
  const [editingLocation, setEditingLocation] = useState(null);
  const [editLocationName, setEditLocationName] = useState('');
  const [savingLocation, setSavingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);

  const loadOfficeLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/reports/locations`);
      setOfficeLocations(response.data.locations || []);
    } catch (err) {
      console.error('Error loading office locations:', err);
    }
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    if (!newLocationName.trim()) return;
    setSavingLocation(true);
    setLocationError(null);
    try {
      await axios.post(`${API_URL}/reports/locations`, { name: newLocationName.trim() });
      setNewLocationName('');
      loadOfficeLocations();
    } catch (err) {
      setLocationError(err.response?.data?.error || 'Failed to add location');
    } finally {
      setSavingLocation(false);
    }
  };

  const handleUpdateLocation = async (id) => {
    if (!editLocationName.trim()) return;
    setSavingLocation(true);
    setLocationError(null);
    try {
      await axios.put(`${API_URL}/reports/locations/${id}`, { name: editLocationName.trim() });
      setEditingLocation(null);
      setEditLocationName('');
      loadOfficeLocations();
      loadReports(); // refresh in case report names changed
    } catch (err) {
      setLocationError(err.response?.data?.error || 'Failed to update location');
    } finally {
      setSavingLocation(false);
    }
  };

  const handleDeleteLocation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    try {
      await axios.delete(`${API_URL}/reports/locations/${id}`);
      loadOfficeLocations();
    } catch (err) {
      setLocationError(err.response?.data?.error || 'Failed to delete location');
    }
  };

  const loadBenchmarks = async () => {
    try {
      const response = await axios.get(`${API_URL}/reports/benchmarks`);
      if (response.data.benchmarks) {
        setBenchmarks(response.data.benchmarks);
      }
    } catch (err) {
      console.error('Error loading benchmarks:', err);
    }
  };

  const loadSummary = async (year, location = '', userId = 'mine') => {
    setLoadingSummary(true);
    try {
      const queryParams = new URLSearchParams();
      if (location) queryParams.set('location', location);
      if (userId !== 'mine' && user?.role === 'admin') queryParams.set('userId', userId);
      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await axios.get(`${API_URL}/reports/summary/${year}${qs}`);
      setSummary(response.data);
    } catch (err) {
      console.error('Error loading summary:', err);
    } finally {
      setLoadingSummary(false);
    }
  };

  const resetForm = () => {
    setFormYear(new Date().getFullYear());
    setFormMonth(new Date().getMonth() + 1);
    setFormPatientVisits('');
    setFormNewPatients('');
    setFormServices('');
    setFormCollections('');
    setFormReExams('');
    setFormOfficeLocation('');
    setEditingReport(null);
  };

  const handleEdit = (report) => {
    setEditingReport(report);
    setFormYear(report.year);
    setFormMonth(report.month);
    setFormOfficeLocation(report.officeLocation || '');
    setFormPatientVisits(report.patientVisits.toString());
    setFormNewPatients(report.newPatients.toString());
    setFormServices(report.services.toString());
    setFormCollections(report.collections.toString());
    setFormReExams(report.reExams.toString());
    setActiveTab('add');
  };

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API_URL}/reports`, {
        year: parseInt(formYear),
        month: parseInt(formMonth),
        officeLocation: formOfficeLocation.trim(),
        patientVisits: parseFloat(formPatientVisits),
        newPatients: parseFloat(formNewPatients),
        services: parseFloat(formServices),
        collections: parseFloat(formCollections),
        reExams: parseFloat(formReExams)
      });
      resetForm();
      setActiveTab('reports');
      loadReports();
      if (user?.role === 'admin') loadAllReports();
      loadOfficeLocations();
    } catch (err) {
      console.error('Error saving report:', err);
      setError(err.response?.data?.error || 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;

    try {
      await axios.delete(`${API_URL}/reports/${reportId}`);
      loadReports();
      if (user?.role === 'admin') loadAllReports();
    } catch (err) {
      console.error('Error deleting report:', err);
      setError('Failed to delete report');
    }
  };

  const handleSaveBenchmarks = async (e) => {
    e.preventDefault();
    setSavingBenchmarks(true);
    try {
      const response = await axios.post(`${API_URL}/reports/benchmarks`, benchmarks);
      if (response.data.benchmarks) {
        setBenchmarks(response.data.benchmarks);
      }
    } catch (err) {
      console.error('Error saving benchmarks:', err);
      setError('Failed to save benchmarks');
    } finally {
      setSavingBenchmarks(false);
    }
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return Number(num).toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  const formatCurrency = (num) => {
    if (num === undefined || num === null) return '$0';
    return '$' + Number(num).toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  // Compute which reports to display based on filters
  const displayedReports = (() => {
    let filtered;
    if (user?.role !== 'admin' || reportFilter === 'mine') {
      filtered = reports; // current user's reports
    } else if (reportFilter === 'all') {
      filtered = allReports; // all users' reports
    } else {
      // Specific user id
      const filterId = parseInt(reportFilter);
      filtered = allReports.filter(r => r.user_id === filterId);
    }
    // Apply location filter
    if (reportLocationFilter) {
      filtered = filtered.filter(r => (r.officeLocation || '') === reportLocationFilter);
    }
    return filtered;
  })();

  // Label for whose reports are shown
  const filterLabel = (() => {
    if (reportFilter === 'mine') return null;
    if (reportFilter === 'all') return 'All Users';
    const u = staffUsers.find(u => u.id === parseInt(reportFilter));
    return u ? u.name : null;
  })();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#3c79bb] mb-4"></div>
          <div className="text-gray-900 font-semibold">Loading reports...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-8">
      {/* Header */}
      <header className="glass-nav border-b border-white/20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-5">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Reports</h1>
          <p className="text-sm sm:text-base text-gray-700 mt-1 font-medium">Track your monthly KPIs and performance</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
        {/* Error Banner */}
        {error && (
          <div className="mb-4 glass-card rounded-2xl p-4 border-2 border-red-300 bg-red-50/50 flex items-center justify-between">
            <span className="text-red-800 font-medium text-sm">{error}</span>
            <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
              <FiX />
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="glass-card rounded-2xl p-1.5 mb-6 flex flex-wrap gap-1">
          {[
            { id: 'reports', label: 'My Reports', icon: FiBarChart2 },
            { id: 'add', label: editingReport ? 'Edit Report' : 'Add Report', icon: FiPlus },
            { id: 'summary', label: 'Summary', icon: FiTrendingUp },
            { id: 'benchmarks', label: 'Benchmarks', icon: FiTarget },
            ...(user?.role === 'admin' ? [{ id: 'locations', label: 'Locations', icon: FiMapPin }] : []),
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'summary') loadSummary(summaryYear, summaryLocation, summaryUser);
                if (tab.id === 'add' && !editingReport) resetForm();
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                activeTab === tab.id
                  ? 'bg-[#3c79bb] text-white shadow-lg shadow-[#3c79bb]/30'
                  : 'text-gray-700 hover:bg-white/50'
              }`}
            >
              <tab.icon className="text-base" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div>
            {/* Filters */}
            <div className="glass-card rounded-2xl p-4 mb-4 flex items-center gap-4 flex-wrap">
              {user?.role === 'admin' && (
                <>
                  <label className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <FiUsers className="text-[#3c79bb]" />
                    User:
                  </label>
                  <select
                    value={reportFilter}
                    onChange={(e) => setReportFilter(e.target.value)}
                    className="px-4 py-2 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] text-gray-900 font-medium min-w-[200px]"
                  >
                    <option value="mine">My Reports</option>
                    <option value="all">All Users</option>
                    {staffUsers.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </>
              )}
              {officeLocations.length > 0 && (
                <>
                  <label className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <FiMapPin className="text-[#3c79bb]" />
                    Location:
                  </label>
                  <select
                    value={reportLocationFilter}
                    onChange={(e) => setReportLocationFilter(e.target.value)}
                    className="px-4 py-2 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] text-gray-900 font-medium min-w-[180px]"
                  >
                    <option value="">All Locations</option>
                    {officeLocations.map((loc) => (
                      <option key={loc.id} value={loc.name}>{loc.name}</option>
                    ))}
                  </select>
                </>
              )}
              <span className="text-sm text-gray-600 font-medium">
                ({displayedReports.length} report{displayedReports.length !== 1 ? 's' : ''})
              </span>
            </div>

            {displayedReports.length === 0 ? (
              <div className="glass-card rounded-2xl p-8 sm:p-12 text-center">
                <FiBarChart2 className="text-5xl text-gray-400 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">No Reports Yet</h2>
                <p className="text-gray-700 mb-4 font-medium">
                  {reportFilter === 'mine'
                    ? 'Start tracking your KPIs by adding your first monthly report.'
                    : 'No reports found for this selection.'}
                </p>
                {reportFilter === 'mine' && (
                  <button
                    onClick={() => { resetForm(); setActiveTab('add'); }}
                    className="px-6 py-2.5 bg-[#3c79bb] text-white rounded-xl hover:bg-[#306196] transition-colors font-semibold"
                  >
                    Add First Report
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {displayedReports.map((report) => (
                  <div key={report.id} className="glass-card rounded-2xl p-5 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <FiCalendar className="text-xl text-[#3c79bb]" />
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">
                            {MONTHS[report.month - 1]} {report.year}
                          </h3>
                          <div className="flex items-center gap-3 flex-wrap">
                            {report.officeLocation && (
                              <div className="flex items-center gap-1 text-sm text-gray-600 font-medium">
                                <FiMapPin className="text-xs" />
                                {report.officeLocation}
                              </div>
                            )}
                            {reportFilter !== 'mine' && report.user_name && (
                              <div className="flex items-center gap-1 text-sm text-[#3c79bb] font-semibold">
                                <FiUsers className="text-xs" />
                                {report.user_name}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {(reportFilter === 'mine' || report.user_id === user?.id) && (
                          <button
                            onClick={() => handleEdit(report)}
                            className="p-2 rounded-xl text-[#3c79bb] hover:bg-[#3c79bb]/10 transition-colors"
                          >
                            <FiEdit2 />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteReport(report.id)}
                          className="p-2 rounded-xl text-red-500 hover:bg-red-50 transition-colors"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                      <div className="bg-white/40 rounded-xl p-3 text-center">
                        <div className="text-xs text-gray-600 font-medium mb-1">Patient Visits</div>
                        <div className="text-lg font-bold text-gray-900">{formatNumber(report.patientVisits)}</div>
                      </div>
                      <div className="bg-white/40 rounded-xl p-3 text-center">
                        <div className="text-xs text-gray-600 font-medium mb-1">New Patients</div>
                        <div className="text-lg font-bold text-gray-900">{formatNumber(report.newPatients)}</div>
                      </div>
                      <div className="bg-white/40 rounded-xl p-3 text-center">
                        <div className="text-xs text-gray-600 font-medium mb-1">PV Average</div>
                        <div className="text-lg font-bold text-gray-900">{formatNumber(report.patientVisitAverage)}</div>
                      </div>
                      <div className="bg-white/40 rounded-xl p-3 text-center">
                        <div className="text-xs text-gray-600 font-medium mb-1">Services</div>
                        <div className="text-lg font-bold text-gray-900">{formatNumber(report.services)}</div>
                      </div>
                      <div className="bg-white/40 rounded-xl p-3 text-center">
                        <div className="text-xs text-gray-600 font-medium mb-1">Svcs/Visit</div>
                        <div className="text-lg font-bold text-gray-900">{formatNumber(report.servicesPerVisit)}</div>
                      </div>
                      <div className="bg-white/40 rounded-xl p-3 text-center">
                        <div className="text-xs text-gray-600 font-medium mb-1">Collections</div>
                        <div className="text-lg font-bold text-green-700">{formatCurrency(report.collections)}</div>
                      </div>
                      <div className="bg-white/40 rounded-xl p-3 text-center">
                        <div className="text-xs text-gray-600 font-medium mb-1">Coll/Visit</div>
                        <div className="text-lg font-bold text-green-700">{formatCurrency(report.collectionsPerVisit)}</div>
                      </div>
                      <div className="bg-white/40 rounded-xl p-3 text-center">
                        <div className="text-xs text-gray-600 font-medium mb-1">Re-Exams</div>
                        <div className="text-lg font-bold text-gray-900">{formatNumber(report.reExams)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Add/Edit Report Tab */}
        {activeTab === 'add' && (
          <div className="glass-card rounded-2xl p-5 sm:p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              {editingReport ? 'Edit Report' : 'Add Monthly Report'}
            </h2>
            <form onSubmit={handleSubmitReport} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Year</label>
                  <select
                    value={formYear}
                    onChange={(e) => setFormYear(parseInt(e.target.value))}
                    className="w-full px-4 py-2.5 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] text-gray-900 font-medium"
                  >
                    {[2024, 2025, 2026, 2027].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Month</label>
                  <select
                    value={formMonth}
                    onChange={(e) => setFormMonth(parseInt(e.target.value))}
                    className="w-full px-4 py-2.5 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] text-gray-900 font-medium"
                  >
                    {MONTHS.map((m, i) => (
                      <option key={i + 1} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    <FiMapPin className="inline mr-1" /> Office Location
                  </label>
                  <select
                    value={formOfficeLocation}
                    onChange={(e) => setFormOfficeLocation(e.target.value)}
                    required
                    className="w-full px-4 py-2.5 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] text-gray-900 font-medium"
                  >
                    <option value="">Select a location...</option>
                    {officeLocations.map((loc) => (
                      <option key={loc.id} value={loc.name}>{loc.name}</option>
                    ))}
                  </select>
                  {officeLocations.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1 font-medium">No locations available. Ask an admin to add locations.</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    <FiUsers className="inline mr-1" /> Patient Visits
                  </label>
                  <input
                    type="number"
                    value={formPatientVisits}
                    onChange={(e) => setFormPatientVisits(e.target.value)}
                    required
                    min="0"
                    step="any"
                    className="w-full px-4 py-2.5 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] text-gray-900 font-medium"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    <FiUsers className="inline mr-1" /> New Patients
                  </label>
                  <input
                    type="number"
                    value={formNewPatients}
                    onChange={(e) => setFormNewPatients(e.target.value)}
                    required
                    min="0"
                    step="any"
                    className="w-full px-4 py-2.5 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] text-gray-900 font-medium"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    <FiActivity className="inline mr-1" /> Services
                  </label>
                  <input
                    type="number"
                    value={formServices}
                    onChange={(e) => setFormServices(e.target.value)}
                    required
                    min="0"
                    step="any"
                    className="w-full px-4 py-2.5 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] text-gray-900 font-medium"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    <FiDollarSign className="inline mr-1" /> Collections
                  </label>
                  <input
                    type="number"
                    value={formCollections}
                    onChange={(e) => setFormCollections(e.target.value)}
                    required
                    min="0"
                    step="any"
                    className="w-full px-4 py-2.5 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] text-gray-900 font-medium"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    <FiTrendingUp className="inline mr-1" /> Re-Exams
                  </label>
                  <input
                    type="number"
                    value={formReExams}
                    onChange={(e) => setFormReExams(e.target.value)}
                    required
                    min="0"
                    step="any"
                    className="w-full px-4 py-2.5 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] text-gray-900 font-medium"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2.5 bg-[#3c79bb] text-white rounded-xl hover:bg-[#306196] transition-colors font-semibold disabled:opacity-50"
                >
                  <FiSave />
                  {saving ? 'Saving...' : editingReport ? 'Update Report' : 'Save Report'}
                </button>
                {editingReport && (
                  <button
                    type="button"
                    onClick={() => { resetForm(); setActiveTab('reports'); }}
                    className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {/* Summary Tab */}
        {activeTab === 'summary' && (
          <div className="space-y-6">
            <div className="glass-card rounded-2xl p-5 sm:p-6">
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                <h2 className="text-xl font-bold text-gray-900">Yearly Summary</h2>
                <select
                  value={summaryYear}
                  onChange={(e) => {
                    const y = parseInt(e.target.value);
                    setSummaryYear(y);
                    loadSummary(y, summaryLocation, summaryUser);
                  }}
                  className="px-4 py-2 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] text-gray-900 font-medium"
                >
                  {[2024, 2025, 2026, 2027].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                {officeLocations.length > 0 && (
                  <select
                    value={summaryLocation}
                    onChange={(e) => {
                      const loc = e.target.value;
                      setSummaryLocation(loc);
                      loadSummary(summaryYear, loc, summaryUser);
                    }}
                    className="px-4 py-2 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] text-gray-900 font-medium"
                  >
                    <option value="">All Locations</option>
                    {officeLocations.map((loc) => (
                      <option key={loc.id} value={loc.name}>{loc.name}</option>
                    ))}
                  </select>
                )}
                {user?.role === 'admin' && staffUsers.length > 0 && (
                  <select
                    value={summaryUser}
                    onChange={(e) => {
                      const uid = e.target.value;
                      setSummaryUser(uid);
                      loadSummary(summaryYear, summaryLocation, uid);
                    }}
                    className="px-4 py-2 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] text-gray-900 font-medium"
                  >
                    <option value="mine">My Summary</option>
                    <option value="all">All Users</option>
                    {staffUsers.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                )}
              </div>

              {loadingSummary ? (
                <div className="flex items-center justify-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#3c79bb]"></div>
                  <span className="ml-3 text-gray-700 font-medium">Loading summary...</span>
                </div>
              ) : summary ? (
                <div className="space-y-6">
                  {/* Top-level stats */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-white/40 rounded-xl p-5 text-center">
                      <div className="text-sm text-gray-600 font-medium mb-1">Total Patient Visits</div>
                      <div className="text-3xl font-bold text-[#3c79bb]">{formatNumber(summary.totalPatientVisits)}</div>
                    </div>
                    <div className="bg-white/40 rounded-xl p-5 text-center">
                      <div className="text-sm text-gray-600 font-medium mb-1">Avg Monthly Visits</div>
                      <div className="text-3xl font-bold text-[#3c79bb]">{formatNumber(summary.averageMonthlyPatientVisits)}</div>
                    </div>
                    <div className="bg-white/40 rounded-xl p-5 text-center">
                      <div className="text-sm text-gray-600 font-medium mb-1">Year</div>
                      <div className="text-3xl font-bold text-gray-900">{summary.year}</div>
                    </div>
                  </div>

                  {/* Quarterly Breakdown */}
                  {summary.quarterlyPatientVisits && (
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-3">Quarterly Patient Visits</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {['Q1', 'Q2', 'Q3', 'Q4'].map(q => (
                          <div key={q} className="bg-white/40 rounded-xl p-4 text-center">
                            <div className="text-sm text-gray-600 font-medium mb-1">{q}</div>
                            <div className="text-2xl font-bold text-gray-900">
                              {formatNumber(summary.quarterlyPatientVisits[q])}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Yearly Averages */}
                  {summary.yearlyAverages && (
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-3">Monthly Averages</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="bg-white/40 rounded-xl p-3 text-center">
                          <div className="text-xs text-gray-600 font-medium mb-1">Patient Visits</div>
                          <div className="text-lg font-bold text-gray-900">{formatNumber(summary.yearlyAverages.patientVisits)}</div>
                        </div>
                        <div className="bg-white/40 rounded-xl p-3 text-center">
                          <div className="text-xs text-gray-600 font-medium mb-1">New Patients</div>
                          <div className="text-lg font-bold text-gray-900">{formatNumber(summary.yearlyAverages.newPatients)}</div>
                        </div>
                        <div className="bg-white/40 rounded-xl p-3 text-center">
                          <div className="text-xs text-gray-600 font-medium mb-1">Services</div>
                          <div className="text-lg font-bold text-gray-900">{formatNumber(summary.yearlyAverages.services)}</div>
                        </div>
                        <div className="bg-white/40 rounded-xl p-3 text-center">
                          <div className="text-xs text-gray-600 font-medium mb-1">Collections</div>
                          <div className="text-lg font-bold text-green-700">{formatCurrency(summary.yearlyAverages.collections)}</div>
                        </div>
                        <div className="bg-white/40 rounded-xl p-3 text-center">
                          <div className="text-xs text-gray-600 font-medium mb-1">PV Average</div>
                          <div className="text-lg font-bold text-gray-900">{formatNumber(summary.yearlyAverages.patientVisitAverage)}</div>
                        </div>
                        <div className="bg-white/40 rounded-xl p-3 text-center">
                          <div className="text-xs text-gray-600 font-medium mb-1">Svcs/Visit</div>
                          <div className="text-lg font-bold text-gray-900">{formatNumber(summary.yearlyAverages.servicesPerVisit)}</div>
                        </div>
                        <div className="bg-white/40 rounded-xl p-3 text-center">
                          <div className="text-xs text-gray-600 font-medium mb-1">Coll/Visit</div>
                          <div className="text-lg font-bold text-green-700">{formatCurrency(summary.yearlyAverages.collectionsPerVisit)}</div>
                        </div>
                        <div className="bg-white/40 rounded-xl p-3 text-center">
                          <div className="text-xs text-gray-600 font-medium mb-1">Re-Exams</div>
                          <div className="text-lg font-bold text-gray-900">{formatNumber(summary.yearlyAverages.reExams)}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-600 font-medium">
                  Select a year to view the summary.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Locations Tab (Admin Only) */}
        {activeTab === 'locations' && user?.role === 'admin' && (
          <div className="glass-card rounded-2xl p-5 sm:p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Manage Office Locations</h2>
            <p className="text-gray-700 font-medium text-sm mb-6">Add, edit, or remove the office locations that staff can select when submitting reports.</p>

            {locationError && (
              <div className="mb-4 p-3 rounded-xl border border-red-300 bg-red-50/50 flex items-center justify-between">
                <span className="text-red-800 font-medium text-sm">{locationError}</span>
                <button onClick={() => setLocationError(null)} className="text-red-600 hover:text-red-800"><FiX /></button>
              </div>
            )}

            {/* Add Location Form */}
            <form onSubmit={handleAddLocation} className="flex items-end gap-3 mb-6">
              <div className="flex-1">
                <label className="block text-sm font-semibold text-gray-900 mb-2">New Location</label>
                <input
                  type="text"
                  value={newLocationName}
                  onChange={(e) => setNewLocationName(e.target.value)}
                  placeholder="e.g. Main Office, Downtown Branch"
                  className="w-full px-4 py-2.5 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] text-gray-900 font-medium"
                />
              </div>
              <button
                type="submit"
                disabled={savingLocation || !newLocationName.trim()}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#3c79bb] text-white rounded-xl hover:bg-[#306196] transition-colors font-semibold disabled:opacity-50"
              >
                <FiPlus />
                Add
              </button>
            </form>

            {/* Locations List */}
            {officeLocations.length === 0 ? (
              <div className="text-center py-8 text-gray-600 font-medium">
                <FiMapPin className="text-4xl mx-auto mb-3 text-gray-400" />
                <p>No locations added yet. Add your first location above.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {officeLocations.map((loc) => (
                  <div key={loc.id} className="flex items-center gap-3 bg-white/40 rounded-xl px-4 py-3">
                    {editingLocation === loc.id ? (
                      <>
                        <input
                          type="text"
                          value={editLocationName}
                          onChange={(e) => setEditLocationName(e.target.value)}
                          className="flex-1 px-3 py-1.5 bg-white/80 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c79bb] text-gray-900 font-medium"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); handleUpdateLocation(loc.id); }
                            if (e.key === 'Escape') { setEditingLocation(null); setEditLocationName(''); }
                          }}
                        />
                        <button
                          onClick={() => handleUpdateLocation(loc.id)}
                          disabled={savingLocation || !editLocationName.trim()}
                          className="p-2 rounded-lg text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50"
                          title="Save"
                        >
                          <FiCheck />
                        </button>
                        <button
                          onClick={() => { setEditingLocation(null); setEditLocationName(''); }}
                          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                          title="Cancel"
                        >
                          <FiX />
                        </button>
                      </>
                    ) : (
                      <>
                        <FiMapPin className="text-[#3c79bb] flex-shrink-0" />
                        <span className="flex-1 font-semibold text-gray-900">{loc.name}</span>
                        <button
                          onClick={() => { setEditingLocation(loc.id); setEditLocationName(loc.name); }}
                          className="p-2 rounded-lg text-[#3c79bb] hover:bg-[#3c79bb]/10 transition-colors"
                          title="Edit"
                        >
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteLocation(loc.id)}
                          className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Benchmarks Tab */}
        {activeTab === 'benchmarks' && (
          <div className="glass-card rounded-2xl p-5 sm:p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Performance Benchmarks</h2>
            <p className="text-gray-700 font-medium text-sm mb-6">Set your standard targets to compare against your monthly reports.</p>

            <form onSubmit={handleSaveBenchmarks} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { key: 'standardAveragePatientVisits', label: 'Avg Patient Visits' },
                  { key: 'standardAverageNewPatients', label: 'Avg New Patients' },
                  { key: 'standardPatientVisitAverage', label: 'PV Average' },
                  { key: 'standardServices', label: 'Services' },
                  { key: 'standardServicesPerVisit', label: 'Services/Visit' },
                  { key: 'standardCollections', label: 'Collections' },
                  { key: 'standardCollectionsPerVisit', label: 'Collections/Visit' },
                  { key: 'standardReExams', label: 'Re-Exams' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">{label}</label>
                    <input
                      type="number"
                      value={benchmarks[key] || ''}
                      onChange={(e) => setBenchmarks(prev => ({ ...prev, [key]: e.target.value }))}
                      min="0"
                      step="any"
                      className="w-full px-4 py-2.5 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] text-gray-900 font-medium"
                      placeholder="0"
                    />
                  </div>
                ))}
              </div>

              <button
                type="submit"
                disabled={savingBenchmarks}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#3c79bb] text-white rounded-xl hover:bg-[#306196] transition-colors font-semibold disabled:opacity-50"
              >
                <FiSave />
                {savingBenchmarks ? 'Saving...' : 'Save Benchmarks'}
              </button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}

export default Reports;
